import Response from './response';
import { Info, ExtendedObject } from './info';
/** 路由函数 */
export interface ExecFunc<
	Ext extends ExtendedObject = {user: object},
	Value extends string | object | Buffer | ArrayBuffer | SharedArrayBuffer | { buffer: ArrayBuffer | SharedArrayBuffer } = {},
	Query extends object = object,
	Body extends object = object,
	Data extends Query | Body | object = Query | Body,
> {
	/**
	 * @param data     数据
	 * @param info     相关信息
	 * @param response 相应
	 */
	(
		data: Data,
		info: Info<Ext, Query, Body>,
		response: Response<Value>
	): undefined | Value | Response<Value> | Promise<undefined | Value | Response<Value>>;
}
/** 路由列表对象 */
export interface ExecObject<Ext extends ExtendedObject = {user: object}> {
	_$?: ExecFunc<Ext>;
	_options?: ExecFunc<Ext>;
	_head?: ExecFunc<Ext>;
	_trace?: ExecFunc<Ext>;
	_get?: ExecFunc<Ext>;
	_post?: ExecFunc<Ext>;
	_put?: ExecFunc<Ext>;
	_delete?: ExecFunc<Ext>;
	_$$?: ExecFunc<Ext>;
	_$options?: ExecFunc<Ext>;
	_$head?: ExecFunc<Ext>;
	_$trace?: ExecFunc<Ext>;
	_$get?: ExecFunc<Ext>;
	_$post?: ExecFunc<Ext>;
	_$put?: ExecFunc<Ext>;
	_$delete?: ExecFunc<Ext>;
	_?: ExecFunc<Ext>;
	[key: string]: undefined | ExecFunc<Ext> | ExecObject<Ext>;
}

/** 路由 */
export interface Router<Ext extends ExtendedObject = {user: object}> {
	/**
	 * @param path    路径数组
	 * @param method  执行方法
	 * @param restful restful 数据，如果传入，则一定当 restful 处理
	 */
	(
		p: string[],
		method?: string,
		restful?: object,
		...m: any[]
	): IterableIterator<ExecFunc<Ext>>;
}
/** 自定义路由 */
export interface ExecHandle<Ext extends ExtendedObject = {user: object}> {
	/**
	 * @param path    路径数组
	 * @param func    路由
	 * @param method  执行方法
	 * @param restful restful 数据，如果传入，则一定当 restful 处理
	 */
	(
		p: string[],
		f: Router<Ext>,
		method?: string,
		restful?: object,
		...m: any[]
	): IterableIterator<ExecFunc<Ext>>
}


function getRestfulArgv(
	opt: [string, string][],
	method: string,
	restful?: object,
): object | undefined {
	if (!(opt.length || restful)) { return ; }
	//路径参数处理
	restful = restful && typeof restful === 'object' ? Object.defineProperties({}, Object.getOwnPropertyDescriptors(restful)) as object : {};
	for (let [key, value] of opt) { Reflect.defineProperty(restful, key, {configurable: true, enumerable: true, writable: true, value}); }
	restful['$method'] = method;
	return restful;
}

function mergeData(restful: object, data: object) {
	return Object.defineProperties({}, {
		...Object.getOwnPropertyDescriptors(data),
		...Object.getOwnPropertyDescriptors(restful)
	}) as object;
}

function *mergeFunc<Ext extends ExtendedObject = {user: object}>(
	func: ExecObject<Ext>, names: string[], argv?: object
): IterableIterator<ExecFunc<Ext>> {
	if (!(func && typeof func === 'object')) { return; }
	yield async function(data, info, response) {
		data = argv ? mergeData(argv, data) : data;
		for(const name of names) {
			const f = func[name];
			if (typeof f !== 'function') { continue; }
			const ret = await f(data, info, response);
			if (ret || typeof ret === 'string') { return ret; }
		}
	}
}

/**
 * 根据信息获取函数
 */
function *getFunc<Ext extends ExtendedObject = {user: object}>(
	func: ExecObject<Ext> | ExecFunc<Ext>,
	method: string,
	opt: [string, string][],
	restful?: {[key: string]: string}
): IterableIterator<ExecFunc<Ext>> {
	//路径参数处理
	const argv = getRestfulArgv(opt, method, restful);
	//如果是对象，将视其$为函数
	if (func && typeof func === 'object') {
		yield* mergeFunc(func, ['_$', '_' + method.toLowerCase(), '_', '$'], argv);
	} else if (typeof func === 'function') {
		yield argv ? (data, info, response) => func(mergeData(argv, data), info, response) : func;
	}
}

const keyTestRegExp = /^([a-zA-Z][a-zA-Z0-9_]*)$/;

function* routerBase<Ext extends ExtendedObject = {user: object}>(
	func: undefined | ExecObject<Ext> | ExecFunc<Ext>,
	path: string[],
	method: string,
	restful?: {[key: string]: string},
	index = 0,
	opt: [string,string][] = []
): IterableIterator<ExecFunc<Ext>> {
	//函数组无效，则直接返回
	if (!func) { return ; }
	let key = path[index++];
	//路径已经取完，则进行返回处理
	if (!key) { return yield* getFunc(func, method, opt, restful); }
	if (typeof func === 'function') { return; }
	//不以美元符号($)、下划线(_)、冒号(:)开头的路径，尝试当做普通路径处理
	if (key in func && !(key[0] === '$' || key[0] === '_' || key[0] === ':')) {
		yield* routerBase(func[key], path, method, restful, index, opt);
	}
	//按照restful API路径处理
	for (let k in func) {
		if (k[0] !== '$' && k[0] !== ':') { continue; }
		const vk = k.substr(1);
		if (!keyTestRegExp.test(vk)) { continue; }
		opt.push([vk, key]);
		yield* routerBase(func[k], path, method, restful, index, opt);
		opt.pop();
	}
	if (method && restful && path.length === index + 1) {
		opt.push(['id', path[index]]);
		yield* mergeFunc(func, ['__$$', '_$' + method.toLowerCase(), '_$'], getRestfulArgv(opt, method, restful));
		opt.pop();
	}
}


/**
 * 默认路由
 * @param path   路径
 * @param method 方法
 * @param restful Restful API 参数
 */
function* router<Ext extends ExtendedObject = {user: object}>(
	this: ExecObject<Ext>,
	path: string[],
	method?: string,
	restful?: {[key: string]: string}
): IterableIterator<ExecFunc<Ext>> {
	yield* routerBase(this, path, method && String(method).toUpperCase() || '', restful);
}

/** 路由创建函数 */
interface create<Ext extends ExtendedObject = {user: object}> {
	/**
	 * 创建函数路由
	 * @param object 函数集合
	 * @param handle 自定义处理器
	 */
	(object: ExecObject<Ext>, handle?: ExecHandle<Ext>): Router<Ext>;
}
/**
 * 创建函数路由
 * @param object 函数集合
 * @param handle 自定义处理器
 */
function create<Ext extends ExtendedObject = {user: object}>(
	object: ExecObject<Ext>,
	handle?: ExecHandle<Ext>
): Router<Ext> {
	const f: Router<Ext> = router.bind(object);
	return handle ? (p: string[], ...m: any) => handle(p, f, ...m) : f;
}
export default create;
